import api from "@/lib/axios";
import { ACCESS_TOKEN_COOKIE, REFRESH_TOKEN_COOKIE } from "@/lib/constants";
import { getEnv } from "@/lib/env";
import { AuthMode } from "@/models/enums/auth-mode";
import { AuthProvider } from "@/models/enums/auth-provider";
import { TokensResponse, TokensResponseSchema } from "@/models/tokens-response";
import { useRouter } from "@tanstack/react-router";
import Cookies from "js-cookie";
import { useCallback, useState } from "react";
import { toast } from "sonner";

export const useOauth = (provider: AuthProvider) => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TokensResponse | null>(null);
  const router = useRouter();

  const initiateOauth = async (refCode?: string) => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      if (refCode) {
        params.append("refCode", refCode);
      }
      const domain = window.location.origin;
      params.append("redirectUrl", domain);

      const platform = getEnv.VITE_PLATFORM;
      if (platform) {
        params.append("platform", platform);
      }
      const response = await api.get<{ url: string }>(
        `/auth/oauth2/${provider}?${params.toString()}`,
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error(`Failed to initiate ${provider} login:`, error);
      toast.error(`Failed to start ${provider} login process`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOauthCallback = async (
    code: string | undefined,
    state: string | undefined,
  ) => {
    if (!code || !state) {
      toast.error(`Invalid ${provider} login response`);
      setTimeout(() => {
        router.navigate({ to: "/login" });
      }, 3000);
      return;
    }

    try {
      setIsLoading(true);
      const response = await api.post<TokensResponse>(
        `/auth/oauth2/${provider}`,
        {
          code,
          state,
        },
      );
      const parsedData = TokensResponseSchema.parse(response.data);

      setResponse(parsedData);

      const authMode = (getEnv.VITE_AUTH_MODE || AuthMode.header) as AuthMode;

      if (
        !parsedData.recovery_key &&
        (authMode === AuthMode.cookie || parsedData.access_token)
      ) {
        onContinue(parsedData);
      }
    } catch (error) {
      console.error(`${provider} login failed:`, error);
      toast.error(`${provider} login failed, please try again`);
    } finally {
      setIsLoading(false);
    }
  };

  const onContinue = useCallback(
    async (resp?: TokensResponse) => {
      const authMode = (getEnv.VITE_AUTH_MODE || AuthMode.header) as AuthMode;
      if (!resp && !response) return;
      if (authMode === AuthMode.header) {
        const { access_token, refresh_token } = resp || response!;
        Cookies.set(ACCESS_TOKEN_COOKIE, access_token ?? "");
        Cookies.set(REFRESH_TOKEN_COOKIE, refresh_token ?? "");
      }
      router.navigate({ to: "/atoms" });
    },
    [response, router],
  );

  return {
    initiateOauth,
    handleOauthCallback,
    isLoading,
    response,
    onContinue,
  };
};
